import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ui, cn } from '../../lib/ui';
import { formatBytes, needsPolling, scanStatus, uploadFileError } from '../../lib/sites';
import * as sitesApi from '../../api/sites';
import { BILLING_UPGRADE_PATH } from '../../lib/upgrade';

/**
 * Premium site packs (docs/Decisions.md D43): upload a .zip, scan it, publish
 * a passed version, roll back to an earlier one, take the site offline.
 * Basic plans see the upgrade prompt instead.
 */

const TONE = {
  success: ui.pillSuccess,
  danger: ui.pillDanger,
  info: ui.pillInfo,
  neutral: ui.pillNeutral,
};

function StatusPill({ status }) {
  const { label, tone } = scanStatus(status);
  return <span className={TONE[tone] || ui.pillNeutral}>{label}</span>;
}

function ScanReport({ scan }) {
  if (!scan) return null;
  const reasons = scan.reasons || [];
  const warnings = scan.warnings || [];
  return (
    <div className="mt-3 flex flex-col gap-2 text-body-s">
      {scan.fileCount ? (
        <p className={ui.muted}>
          {scan.fileCount} files, {formatBytes(scan.totalBytes)}
          {scan.malware?.disabled ? '. Malware scanning is switched off in security settings.' : ''}
        </p>
      ) : null}
      {reasons.length ? (
        <div className={ui.alertError} role="alert">
          <p className="font-semibold">Why it was rejected</p>
          <ul className="mt-1 list-disc pl-5">
            {reasons.map((r, i) => (
              <li key={i}>
                {r.path ? <span className="font-mono text-data">{r.path}: </span> : null}
                {r.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {warnings.length ? (
        <div className={ui.alertWarning}>
          <p className="font-semibold">Check before publishing</p>
          <ul className="mt-1 list-disc pl-5">
            {warnings.map((w, i) => (
              <li key={i}>
                {w.path ? <span className="font-mono text-data">{w.path}: </span> : null}
                {w.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default function SiteUploads({ canUpload, hostingConfigured, limits }) {
  const [data, setData] = useState({ uploads: [], liveUploadId: null, url: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('spa');
  const [busy, setBusy] = useState(null);

  const enabled = canUpload && hostingConfigured;

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      setData(await sitesApi.listSiteUploads());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!needsPolling(data.uploads)) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [data.uploads, load]);

  async function run(label, fn, success) {
    setBusy(label);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (success) setNotice(success);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  async function upload(e) {
    e.preventDefault();
    const problem = uploadFileError(file, limits);
    if (problem) {
      setError(problem);
      return;
    }
    await run(
      'upload',
      async () => {
        const res = await sitesApi.requestSiteUpload(file, mode);
        await sitesApi.putToPresignedUrl(res.uploadUrl, file, 'application/zip');
        await sitesApi.scanSiteUpload(res.upload.uploadId);
        setFile(null);
      },
      'Uploaded. The scan usually takes a minute or two; this list updates on its own.'
    );
  }

  if (!canUpload) {
    return (
      <section className={ui.card} aria-labelledby="site-uploads-title">
        <div className={ui.row}>
          <h2 id="site-uploads-title" className={cn(ui.h2, 'mb-0!')}>
            Upload your own site
          </h2>
          <span className={ui.pillPremium}>Premium</span>
        </div>
        <p className="mt-3 text-body text-dcon-ink-body">
          Premium organizations can upload a single-page app or a plain HTML site as a .zip. We scan
          every file and host it at your own address.
        </p>
        <p className="mt-3">
          <Link className={cn(ui.btnAccent, ui.btnSm)} to={BILLING_UPGRADE_PATH}>
            See plans and upgrade
          </Link>
        </p>
        <p className={cn(ui.hint, 'mt-2')}>Only an organization admin can change the plan.</p>
      </section>
    );
  }

  if (!hostingConfigured) {
    return (
      <section className={ui.card} aria-labelledby="site-uploads-title">
        <h2 id="site-uploads-title" className={ui.h2}>
          Upload your own site
        </h2>
        <p className={ui.alertInfo}>Site file hosting is not set up in this environment yet.</p>
      </section>
    );
  }

  return (
    <section className={ui.card} aria-labelledby="site-uploads-title">
      <div className={cn(ui.row, ui.spread)}>
        <h2 id="site-uploads-title" className={cn(ui.h2, 'mb-0!')}>
          Upload your own site
        </h2>
        {data.liveUploadId && data.url ? (
          <a
            className={cn(ui.btnSecondary, ui.btnSm)}
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the live site
          </a>
        ) : null}
      </div>
      <p className={cn(ui.hint, 'mb-4')}>
        A .zip with index.html at the top (or in one folder), up to{' '}
        {formatBytes(limits?.maxArchiveBytes)} and {limits?.maxFiles} files. Server-side code,
        symbolic links and archives inside the zip are refused.
      </p>

      {error ? (
        <p className={ui.alertError} role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className={ui.alertSuccess} role="status">
          {notice}
        </p>
      ) : null}

      <form className="mb-6 grid gap-4 md:grid-cols-3 md:items-end" onSubmit={upload}>
        <label className={cn(ui.field, 'mb-0!')}>
          <span className={ui.label}>Site .zip</span>
          <input
            type="file"
            accept=".zip,application/zip"
            className={ui.input}
            onChange={e => setFile(e.target.files?.[0] || null)}
          />
        </label>
        <label className={cn(ui.field, 'mb-0!')}>
          <span className={ui.label}>Kind of site</span>
          <select className={ui.input} value={mode} onChange={e => setMode(e.target.value)}>
            <option value="spa">Single-page app (unknown paths show index.html)</option>
            <option value="static">HTML site (unknown paths are not found)</option>
          </select>
        </label>
        <button type="submit" className={ui.btnPrimary} disabled={busy !== null}>
          {busy === 'upload' ? 'Uploading' : 'Upload and scan'}
        </button>
      </form>

      {data.retainedVersions ? (
        <p className={cn(ui.hint, 'mt-2')}>
          {`The live version and the ${data.retainedVersions - 1} most recent others are kept. Older uploads are deleted automatically when you upload or publish.`}
        </p>
      ) : null}
      {loading && !data.uploads.length ? <p className={ui.muted}>Loading uploads.</p> : null}
      {!loading && !data.uploads.length ? <p className={ui.muted}>No uploads yet.</p> : null}

      <ul className="flex flex-col gap-3">
        {data.uploads.map(u => {
          const live = u.uploadId === data.liveUploadId;
          return (
            <li key={u.uploadId} className={ui.cardFlat} data-testid={`site-upload-${u.uploadId}`}>
              <div className={cn(ui.row, ui.spread)}>
                <div className={ui.row}>
                  <span className="font-mono text-data">{u.fileName}</span>
                  <StatusPill status={u.status} />
                  {live ? <span className={ui.pillPrimary}>Live</span> : null}
                  <span className={ui.subtle}>
                    {u.mode === 'spa' ? 'Single-page app' : 'HTML site'}, {formatBytes(u.size)}
                  </span>
                </div>
                <div className={ui.row}>
                  {u.status === 'pending' ? (
                    <button
                      type="button"
                      className={cn(ui.btnSecondary, ui.btnSm)}
                      disabled={busy !== null}
                      onClick={() =>
                        run(
                          `scan-${u.uploadId}`,
                          () => sitesApi.scanSiteUpload(u.uploadId),
                          'Scan started.'
                        )
                      }
                    >
                      Start scan
                    </button>
                  ) : null}
                  {u.status === 'passed' && !live ? (
                    <button
                      type="button"
                      className={cn(ui.btnPrimary, ui.btnSm)}
                      disabled={busy !== null}
                      onClick={() =>
                        run(
                          `publish-${u.uploadId}`,
                          () => sitesApi.publishSiteUpload(u.uploadId),
                          data.liveUploadId
                            ? 'This version is live. The previous one is kept for rollback.'
                            : 'Your site is live.'
                        )
                      }
                    >
                      {data.liveUploadId ? 'Publish this version' : 'Publish'}
                    </button>
                  ) : null}
                  {live ? (
                    <button
                      type="button"
                      className={cn(ui.btnSecondary, ui.btnSm)}
                      disabled={busy !== null}
                      onClick={() =>
                        run(
                          'offline',
                          () => sitesApi.takeHostedSiteOffline(),
                          'The uploaded site is offline.'
                        )
                      }
                    >
                      Take offline
                    </button>
                  ) : null}
                  {!live && u.status !== 'scanning' ? (
                    <button
                      type="button"
                      className={cn(ui.btnGhost, ui.btnSm)}
                      disabled={busy !== null}
                      onClick={() =>
                        run(
                          `delete-${u.uploadId}`,
                          () => sitesApi.deleteSiteUpload(u.uploadId),
                          'Upload deleted.'
                        )
                      }
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
              <ScanReport scan={u.scan} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
